import { useEffect, useId, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { FormCard, PrimaryButton, ChevronRightIcon } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimOtherMultiVehicleOtherPartyHref,
  buildClaimOtherMultiVehicleReviewHref,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
} from '../claims/claimFlowModel'
import { parseOtherClaimSubtypeQuery, type OtherClaimSubtypeId } from '../claims/otherClaimSubtypeOptions'
import {
  getMultiVehicleDescribeParts,
  persistMultiVehicleDescribeNarratives,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import { useEnsureOtherBranchGstAnswered } from '../claims/useEnsureOtherBranchGstAnswered'
import { CLAIM_DAMAGE_SECTION_INTRO } from '../claims/claimDescribeCopy'
import {
  claimFormFieldClass,
  ClaimFormFieldError,
  ClaimFormSubmitSummaryAlert,
  useClaimFormSubmitAttempt,
} from '../claims/claimFormValidationFeedback'

const DESCRIBE_TEXTAREA_CLASS =
  'min-h-[168px] w-full resize-y rounded-xl border border-solid border-border-primary bg-background-primary px-[16px] py-[14px] font-body text-sm leading-sm text-text-primary-900 shadow-xs placeholder:text-text-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2'

const MAX_FIELD_LENGTH = 1000

const INCIDENT_PROMPTS = [
  'The direction each vehicle was travelling (including yours), and approximate speed if you know it',
  'Where you were going and where you had come from',
  'Traffic (e.g. heavy or light) and road conditions (surface, lanes, intersection, signals)',
  'Weather and visibility',
  'What led up to the impact (e.g. lane changes, braking, signals)',
  'Anything else we should know (witnesses, police, hazards, dashcam, etc.)',
] as const

const DAMAGE_PROMPTS = [
  'Damage to your vehicle (areas of the vehicle and what you can see)',
  'Contact with other vehicles or objects (what hit what, if clear)',
  'Whether vehicles were driveable or needed a tow',
  'Other property damaged (e.g. sign, fence), if applicable',
] as const

const MULTI_VEHICLE_SUBTYPE = 'multi-vehicle-collision' as const satisfies OtherClaimSubtypeId

function initialDescribeState(): { incident: string; damage: string } {
  return getMultiVehicleDescribeParts(readClaimDraft())
}

/**
 * Multi-vehicle collision: free-text incident narrative and damage details before confirm/submit.
 */
export default function ClaimOtherMultiVehicleDescribeStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const incidentFieldId = useId()
  const damageFieldId = useId()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimSubtype =
    parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
    parseOtherClaimSubtypeQuery(readClaimDraft()?.otherClaimSubtypeId ?? null)
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  useEnsureOtherBranchGstAnswered({
    claimEntryBranch: 'other',
    policyRef,
    policyTypeQuery: typeInQuery,
    otherClaimSubtype: claimSubtype,
    navPolicy: navState?.policy,
  })

  const [{ incident, damage }, setPair] = useState(initialDescribeState)

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      persistMultiVehicleDescribeNarratives(policyRef, incident, damage)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [damage, incident, policyRef])

  const persistNow = () => {
    persistMultiVehicleDescribeNarratives(policyRef, incident, damage)
  }

  const backToOtherParty = () => {
    persistNow()
    if (claimSubtype === null) return
    const path = buildClaimOtherMultiVehicleOtherPartyHref(policyRef, typeInQuery, claimSubtype)
    if (navState?.policy) {
      navigate(path, { state: { policy: navState.policy } })
    } else {
      navigate(path)
    }
  }

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const canProceed = incident.trim().length > 0 && damage.trim().length > 0
  const { submitAttempted, showSubmitSummary, attemptSubmit, clearSubmitAttempt } =
    useClaimFormSubmitAttempt(canProceed)
  const fieldErrors = useMemo(() => {
    if (!submitAttempted) return { incident: false, damage: false }
    return {
      incident: incident.trim().length === 0,
      damage: damage.trim().length === 0,
    }
  }, [submitAttempted, incident, damage])

  const handleNext = () => {
    attemptSubmit(() => {
      if (claimSubtype === null) return
      persistNow()
      const path = buildClaimOtherMultiVehicleReviewHref(policyRef, typeInQuery, claimSubtype)
      navigate(path, {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    })
  }

  const draft = readClaimDraft()

  if (claimSubtype === null || claimSubtype !== MULTI_VEHICLE_SUBTYPE) {
    return null
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={backToOtherParty}
        title="Incident and damage"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="other"
            otherClaimSubtypeId={claimSubtype}
            incidentSummary={formatIncidentRailSummary(draft)}
            multiVehicleCollisionSummary={formatMultiVehicleCollisionRailSummary(draft)}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(draft)}
            multiVehicleIncidentDescription={incident}
            multiVehicleDamageDescription={damage}
            multiVehicleClaimDescription={draft?.multiVehicleClaimDescription ?? null}
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
          />
        }
      >
        <FormCard>
          <section className="flex w-full flex-col items-start gap-[24px]">
            {showSubmitSummary ? <ClaimFormSubmitSummaryAlert /> : null}
            <div className="flex w-full flex-col items-start gap-[12px]">
              <h2 className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">
                What happened
              </h2>
              <p className="m-0 font-body text-sm leading-sm text-text-primary-900">
                Describe how the collision occurred and the conditions around it. The more detail you share, the
                easier it is for us to understand what happened. You do not need to use full sentences. Dot points are
                fine.
              </p>
              <p className="m-0 font-body text-sm font-semibold leading-sm text-text-primary-900">
                Consider including:
              </p>
              <ul className="m-0 flex list-disc flex-col gap-[8px] pl-[20px] font-body text-sm leading-sm text-text-secondary-700 marker:text-text-secondary-700">
                {INCIDENT_PROMPTS.map((item) => (
                  <li key={item} className="pl-[4px]">
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex w-full flex-col items-stretch gap-[8px]">
                <label htmlFor={incidentFieldId} className="sr-only">
                  Incident description (maximum {MAX_FIELD_LENGTH} characters)
                </label>
                <textarea
                  id={incidentFieldId}
                  value={incident}
                  maxLength={MAX_FIELD_LENGTH}
                  onChange={(e) => {
                    clearSubmitAttempt()
                    setPair((p) => ({ ...p, incident: e.target.value }))
                  }}
                  placeholder="Describe what happened leading up to and during the incident…"
                  rows={7}
                  aria-invalid={fieldErrors.incident}
                  aria-describedby={fieldErrors.incident ? `${incidentFieldId}-error` : undefined}
                  className={claimFormFieldClass(DESCRIBE_TEXTAREA_CLASS, fieldErrors.incident)}
                />
                {fieldErrors.incident ? (
                  <ClaimFormFieldError id={`${incidentFieldId}-error`}>
                    Describe what happened to continue.
                  </ClaimFormFieldError>
                ) : null}
                <p
                  className="m-0 self-end font-body text-xs leading-xs text-text-tertiary-600"
                  aria-live="polite"
                >
                  {incident.length}/{MAX_FIELD_LENGTH} characters
                </p>
              </div>
            </div>

            <div className="h-px w-full bg-border-secondary" aria-hidden />

            <div className="flex w-full flex-col items-start gap-[12px]">
              <h2 className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">
                Damage and impact
              </h2>
              <p className="m-0 font-body text-sm leading-sm text-text-primary-900">
                {CLAIM_DAMAGE_SECTION_INTRO}
              </p>
              <p className="m-0 font-body text-sm font-semibold leading-sm text-text-primary-900">
                Consider including:
              </p>
              <ul className="m-0 flex list-disc flex-col gap-[8px] pl-[20px] font-body text-sm leading-sm text-text-secondary-700 marker:text-text-secondary-700">
                {DAMAGE_PROMPTS.map((item) => (
                  <li key={item} className="pl-[4px]">
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex w-full flex-col items-stretch gap-[8px]">
                <label htmlFor={damageFieldId} className="sr-only">
                  Damage details (maximum {MAX_FIELD_LENGTH} characters)
                </label>
                <textarea
                  id={damageFieldId}
                  value={damage}
                  maxLength={MAX_FIELD_LENGTH}
                  onChange={(e) => {
                    clearSubmitAttempt()
                    setPair((p) => ({ ...p, damage: e.target.value }))
                  }}
                  placeholder="Describe damage, contact points, drivability, and other property…"
                  rows={7}
                  aria-invalid={fieldErrors.damage}
                  aria-describedby={fieldErrors.damage ? `${damageFieldId}-error` : undefined}
                  className={claimFormFieldClass(DESCRIBE_TEXTAREA_CLASS, fieldErrors.damage)}
                />
                {fieldErrors.damage ? (
                  <ClaimFormFieldError id={`${damageFieldId}-error`}>
                    Describe the damage to continue.
                  </ClaimFormFieldError>
                ) : null}
                <p
                  className="m-0 self-end font-body text-xs leading-xs text-text-tertiary-600"
                  aria-live="polite"
                >
                  {damage.length}/{MAX_FIELD_LENGTH} characters
                </p>
              </div>
            </div>
          </section>
        </FormCard>

        <div className="sticky bottom-0 w-full bg-background-secondary pt-[8px] pb-[8px] md:static md:py-0">
          <PrimaryButton
            type="button"
            onClick={handleNext}
            size="lg"
            fullWidth
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label="Next"
          >
            Next
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
